import { Clock } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { BriefcaseOutline } from 'src/shared/components/icons/referrals/briefcase-outline'
import { ProfileAvatar } from 'src/shared/features/users/components/profile-photos/profile-avatar'
import type { Profession } from 'src/shared/features/users/schemas/profession'
import { professionMap } from 'src/shared/features/users/schemas/profession'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import type { StreamPost } from 'src/shared/schemas/stream/feed'
import { cn } from 'src/shared/utils/cn'
import { formatMessageTime } from 'src/shared/utils/dates/format-message-time'

import { PostOptionsMenu } from './post-options-menu'

interface PostHeaderProps {
  userId: string
  fullName: string
  username?: string
  profession?: Profession
  shortAddress?: string | null
  timestamp?: string
  showUsername?: boolean
  className?: string
  post?: StreamPost
  clickable?: boolean
  onEdit?: () => void
  onDelete?: () => void
}

const formatNameForMobile = (fullName: string, username?: string): string => {
  if (!username) return fullName

  const combinedLength = fullName.length + (username?.length || 0)
  if (combinedLength <= 35) return fullName

  const names = fullName.split(' ')
  if (names.length <= 1) return fullName

  return names[0]
}

export const PostHeader = ({
  userId,
  fullName,
  username,
  profession,
  shortAddress,
  timestamp,
  showUsername = true,
  post,
  clickable = true,
  onEdit,
  onDelete,
}: PostHeaderProps) => {
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  const handleAuthorClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (clickable && username) {
      navigate(`/${username}`)
    }
  }

  const displayName = isMobile ? formatNameForMobile(fullName, username) : fullName

  return (
    <div className="flex items-start justify-between">
      <div
        className={cn('flex items-center gap-3', clickable ? 'cursor-pointer' : 'cursor-default', isMobile && 'gap-2')}
        onClick={handleAuthorClick}
      >
        <div>
          <ProfileAvatar userId={parseInt(userId)} size={isMobile ? 35 : 40} />
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-base">
            <span className={cn('font-semibold', isMobile && 'text-xs')}>{displayName}</span>
            {showUsername && username && (
              <span className={cn('flex items-center truncate text-muted', isMobile && 'text-xs')}>@{username}</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {profession && (
              <div className="flex items-center gap-1">
                <BriefcaseOutline className={cn('size-3 text-muted', isMobile && 'h-5 w-[21.433px]')} />
                <p className={cn('text-xs', isMobile && 'text-xs')}>{professionMap[profession]?.label}</p>
              </div>
            )}
            {shortAddress && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted">·</span>
                <span className="text-xs text-muted">{shortAddress}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={cn('flex items-start gap-2', isMobile && 'gap-1')}>
        {timestamp && (
          <div className="flex flex-row items-center text-xs text-muted">
            <Clock className="mr-1 size-2.5" />
            <span>{formatMessageTime(timestamp, { short: true })}</span>
          </div>
        )}
        {post && <PostOptionsMenu post={post} onEdit={onEdit} onDelete={onDelete} />}
      </div>
    </div>
  )
}
